import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, Plus, Search, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import { api } from '@/lib/api';
import { clsx } from 'clsx';

const CLAIM_STATUS_STYLE: Record<string, string> = {
  draft:     'bg-gray-100 text-gray-600',
  submitted: 'bg-blue-100 text-blue-700',
  under_review: 'bg-yellow-100 text-yellow-700',
  approved:  'bg-green-100 text-green-700',
  partially_approved: 'bg-orange-100 text-orange-600',
  rejected:  'bg-red-100 text-red-500',
  settled:   'bg-purple-100 text-purple-700',
};

const PREAUTH_STATUS_STYLE: Record<string, string> = {
  not_required: 'bg-gray-100 text-gray-500',
  pending:      'bg-yellow-100 text-yellow-700',
  approved:     'bg-green-100 text-green-700',
  rejected:     'bg-red-100 text-red-500',
};

function fmt(n: any) {
  return '₹' + Number(n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

function StatusUpdateModal({ claim, onClose }: { claim: any; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    claim_status:     claim.claim_status ?? 'draft',
    preauth_status:   claim.preauth_status ?? 'not_required',
    preauth_number:   claim.preauth_number ?? '',
    preauth_amount:   String(claim.preauth_amount ?? ''),
    approved_amount:  String(claim.approved_amount ?? ''),
    disallowed_amount:String(claim.disallowed_amount ?? ''),
    copay_amount:     String(claim.copay_amount ?? ''),
    rejection_reason: claim.rejection_reason ?? '',
    notes:            claim.notes ?? '',
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => api.patch(`/billing/tpa-claims/${claim.id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tpa-claims'] });
      onClose();
    },
  });

  function handleSave() {
    const body: any = {
      claim_status:     form.claim_status,
      preauth_status:   form.preauth_status,
      preauth_number:   form.preauth_number || undefined,
      preauth_amount:   form.preauth_amount  ? parseFloat(form.preauth_amount)   : undefined,
      approved_amount:  form.approved_amount ? parseFloat(form.approved_amount)  : undefined,
      disallowed_amount:form.disallowed_amount ? parseFloat(form.disallowed_amount) : undefined,
      copay_amount:     form.copay_amount    ? parseFloat(form.copay_amount)     : undefined,
      rejection_reason: form.rejection_reason || undefined,
      notes:            form.notes || undefined,
    };
    updateMutation.mutate(body);
  }

  function field(k: string) {
    return {
      value: (form as any)[k],
      onChange: (e: any) => setForm(f => ({ ...f, [k]: e.target.value })),
    };
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="font-bold text-navy">{claim.claim_number}</h2>
            <p className="text-xs text-gray-400">{claim.patient_name_enc ?? claim.patient_uid} — {claim.tpa_name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Claim Status</label>
              <select {...field('claim_status')}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-navy/20 outline-none">
                {['draft','submitted','under_review','approved','partially_approved','rejected','settled'].map(s => (
                  <option key={s} value={s}>{s.replace(/_/g, ' ').toUpperCase()}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Pre-auth Status</label>
              <select {...field('preauth_status')}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-navy/20 outline-none">
                {['not_required','pending','approved','rejected'].map(s => (
                  <option key={s} value={s}>{s.replace(/_/g, ' ').toUpperCase()}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Pre-auth Number</label>
              <input {...field('preauth_number')} placeholder="PAH-XXXXX"
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-navy/20 outline-none" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Pre-auth Amount (₹)</label>
              <input type="number" {...field('preauth_amount')}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-navy/20 outline-none" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Approved (₹)</label>
              <input type="number" {...field('approved_amount')}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-navy/20 outline-none" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Disallowed (₹)</label>
              <input type="number" {...field('disallowed_amount')}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-navy/20 outline-none" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Co-pay (₹)</label>
              <input type="number" {...field('copay_amount')}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-navy/20 outline-none" />
            </div>
          </div>

          {form.claim_status === 'rejected' && (
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Rejection Reason</label>
              <textarea {...field('rejection_reason')} rows={2}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-navy/20 outline-none resize-none" />
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Notes</label>
            <textarea {...field('notes')} rows={2}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-navy/20 outline-none resize-none" />
          </div>
        </div>
        <div className="px-6 pb-5 flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-xl text-sm font-semibold hover:bg-navy/90 disabled:opacity-40"
          >
            {updateMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            Update Claim
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TpaClaimsPage() {
  const [search, setSearch]           = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedClaim, setSelectedClaim] = useState<any>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['tpa-claims', statusFilter],
    queryFn: () =>
      api.get('/billing/tpa-claims', {
        params: { claim_status: statusFilter || undefined },
      }).then(r => r.data),
    staleTime: 30_000,
  });

  const claims: any[] = (data?.claims ?? []).filter((c: any) =>
    !search ||
    c.claim_number?.toLowerCase().includes(search.toLowerCase()) ||
    (c.patient_name_enc ?? '').toLowerCase().includes(search.toLowerCase()) ||
    c.patient_uid?.toLowerCase().includes(search.toLowerCase()) ||
    c.tpa_name?.toLowerCase().includes(search.toLowerCase())
  );

  const stats = {
    total:    data?.total ?? 0,
    pending:  (data?.claims ?? []).filter((c: any) => ['draft','submitted','under_review'].includes(c.claim_status)).length,
    approved: (data?.claims ?? []).filter((c: any) => ['approved','partially_approved'].includes(c.claim_status)).length,
    settled:  (data?.claims ?? []).filter((c: any) => c.claim_status === 'settled').length,
    pendingAmount: (data?.claims ?? [])
      .filter((c: any) => ['submitted','under_review'].includes(c.claim_status))
      .reduce((s: number, c: any) => s + (c.claimed_amount ?? 0), 0),
    approvedAmount: (data?.claims ?? [])
      .filter((c: any) => ['approved','partially_approved','settled'].includes(c.claim_status))
      .reduce((s: number, c: any) => s + (c.approved_amount ?? c.claimed_amount ?? 0), 0),
  };

  return (
    <div className="p-6 max-w-screen-xl mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy flex items-center gap-2">
            <ShieldCheck size={22} className="text-orange" /> TPA / Insurance Claims
          </h1>
          <p className="text-sm text-gray-400 mt-0.5">Manage cashless & reimbursement claims</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Claims',    value: stats.total,    color: 'bg-white border-gray-200' },
          { label: 'Pending',         value: stats.pending,  color: 'bg-yellow-50 border-yellow-200' },
          { label: 'Approved',        value: stats.approved, color: 'bg-green-50 border-green-200' },
          { label: 'Settled',         value: stats.settled,  color: 'bg-purple-50 border-purple-200' },
        ].map(s => (
          <div key={s.label} className={clsx('rounded-xl border p-4 shadow-card', s.color)}>
            <div className="text-2xl font-bold text-navy">{s.value}</div>
            <div className="text-xs text-gray-500 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Amount summary */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-card flex gap-8 text-sm">
        <div>
          <span className="text-gray-400">Pending amount:</span>{' '}
          <span className="font-semibold text-yellow-700">{fmt(stats.pendingAmount)}</span>
        </div>
        <div>
          <span className="text-gray-400">Approved amount:</span>{' '}
          <span className="font-semibold text-green-700">{fmt(stats.approvedAmount)}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search claim no., patient or TPA..."
            className="w-full pl-8 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-navy"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-navy"
        >
          <option value="">All Status</option>
          {['draft','submitted','under_review','approved','partially_approved','rejected','settled'].map(s => (
            <option key={s} value={s}>{s.replace(/_/g, ' ').toUpperCase()}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 size={22} className="animate-spin mr-2" /> Loading...
          </div>
        ) : claims.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <ShieldCheck size={36} className="mx-auto mb-3 opacity-25" />
            <p className="text-sm font-medium text-navy">No TPA claims found</p>
            <p className="text-xs mt-1">Create TPA claims from the Billing page when invoicing TPA/insurance patients.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/60">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Claim No.</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Patient</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">TPA / Scheme</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Claimed (₹)</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Approved (₹)</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500">Pre-auth</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500">Status</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500">Date</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500">Action</th>
                </tr>
              </thead>
              <tbody>
                {claims.map((c: any) => (
                  <tr key={c.id} className="border-b border-gray-50 hover:bg-gray-50/40 transition">
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-navy">{c.claim_number}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{c.patient_name_enc ?? '—'}</div>
                      <div className="text-xs text-gray-400">{c.patient_uid}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-700">{c.tpa_name}</div>
                      <div className="text-xs text-gray-400">{c.scheme?.replace(/_/g,' ')} · {c.claim_type}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-800">{fmt(c.claimed_amount)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-green-700">
                      {c.approved_amount ? fmt(c.approved_amount) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium',
                        PREAUTH_STATUS_STYLE[c.preauth_status ?? 'not_required'])}>
                        {(c.preauth_status ?? 'N/A').replace(/_/g,' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={clsx('px-2 py-1 rounded-lg text-xs font-medium',
                        CLAIM_STATUS_STYLE[c.claim_status] ?? 'bg-gray-100')}>
                        {c.claim_status?.replace(/_/g,' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs">
                      {c.created_at ? format(new Date(c.created_at), 'dd MMM yyyy') : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setSelectedClaim(c)}
                        className="px-3 py-1 bg-navy text-white rounded-lg text-xs hover:bg-navy/90 transition"
                      >
                        Update
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedClaim && (
        <StatusUpdateModal claim={selectedClaim} onClose={() => setSelectedClaim(null)} />
      )}
    </div>
  );
}
